import { Link } from "@ui/Atoms/Link/Link";
import type * as React from "react";

type Variant = "primary" | "secondary" | "ghost" | "accent" | "destructive";
type Size = "sm" | "md" | "lg" | "icon";
type Shape = "default" | "pill" | "square";

type OwnProps = {
	variant?: Variant;
	size?: Size;
	shape?: Shape;
};

type AsButton = OwnProps & {
	as?: "button";
} & React.ButtonHTMLAttributes<HTMLButtonElement>;
type AsAnchor = OwnProps & {
	as: "a";
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonProps = AsButton | AsAnchor;

const base: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 8,
	whiteSpace: "nowrap",
	fontFamily: "var(--font-sans)",
	fontWeight: 700,
	border: "none",
	cursor: "pointer",
	transition: "background 0.15s, opacity 0.15s, border-color 0.15s",
	outline: "none",
	textDecoration: "none",
};

const variants: Record<Variant, React.CSSProperties> = {
	primary: {
		background: "var(--surface-inverted)",
		color: "var(--content-inverted)",
	},
	secondary: {
		background: "var(--surface-default)",
		color: "var(--content-primary)",
		border: "1px solid var(--border-strong)",
	},
	ghost: {
		background: "transparent",
		color: "var(--content-secondary)",
	},
	accent: {
		background: "var(--accent-500)",
		color: "var(--content-inverted)",
	},
	destructive: {
		background: "var(--accent-destructive)",
		color: "var(--content-inverted)",
	},
};

const sizes: Record<Size, React.CSSProperties> = {
	sm: {
		height: 34,
		borderRadius: "var(--radius-sm)",
		padding: "0 14px",
		fontSize: 13,
	},
	md: {
		height: 40,
		borderRadius: "var(--radius-sm)",
		padding: "0 16px",
		fontSize: 13.5,
	},
	lg: {
		height: 48,
		borderRadius: "var(--radius-md)",
		padding: "0 22px",
		fontSize: 14.5,
	},
	icon: {
		height: 40,
		width: 40,
		borderRadius: "var(--radius-sm)",
		padding: 0,
	},
};

const shapes: Record<Shape, React.CSSProperties> = {
	default: {},
	pill: { borderRadius: "var(--radius-pill)" },
	square: { borderRadius: "var(--radius-sm)" },
};

export function Button({
	as,
	variant = "primary",
	size = "md",
	shape = "default",
	style,
	...props
}: ButtonProps) {
	const computedStyle: React.CSSProperties = {
		...base,
		...variants[variant],
		...sizes[size],
		...shapes[shape],
		...style,
	};

	if (as === "a") {
		return (
			<Link
				style={computedStyle}
				{...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
			/>
		);
	}

	const { disabled } = props as React.ButtonHTMLAttributes<HTMLButtonElement>;

	return (
		<button
			style={{
				...computedStyle,
				opacity: disabled ? 0.5 : 1,
				pointerEvents: disabled ? "none" : "auto",
			}}
			{...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
		/>
	);
}
